import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { SOLICITUD_ALTERNATIVAS_API } from '../../../shared/services/solicitudAlternativas';
import ActionButton from '../ActionButton';
import ActionCommentField from '../ActionCommentField';
import ComentariosHistorialList from '../ComentariosHistorialList';
import LoadingInline from '../layout/LoadingInline';
import { ModalSection, ModalPanel } from '../layout/ModalSection';
import AlternativaDetalleAlternativasGrid from '../AlternativaDetalleAlternativasGrid';
import SinAlternativaDetallePanel from '../SinAlternativaDetallePanel';

const NOTICE_BOX_STYLE = {
    backgroundColor: '#fff3cd',
    color: '#856404',
    fontSize: '13px',
    lineHeight: 1.55,
};

const DetalleMasInformacionView = ({
    currentItem,
    readOnly = false,
    onClose,
    onSolicitudUpdated,
}) => {
    const [informacion, setInformacion] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [detalleData, setDetalleData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const id = currentItem?.id;
        if (id == null) {
            setDetalleData(null);
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res =
                    await SOLICITUD_ALTERNATIVAS_API.getDetalleMasInformacion(
                        id,
                    );
                if (cancelled) return;
                if (res?.success) {
                    setDetalleData(res.data ?? null);
                } else {
                    toast.error(
                        res?.message || 'No se han podido cargar los datos.',
                    );
                    setDetalleData(null);
                }
            } catch (e) {
                if (!cancelled) {
                    toast.error(
                        e?.response?.data?.message ||
                            e?.message ||
                            'No se han podido cargar los datos.',
                    );
                    setDetalleData(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [currentItem?.id]);

    const solicitudAt = detalleData?.solicitud_at;
    const alternativa = detalleData?.alternativa;
    const sinAlternativa = detalleData?.sin_alternativa;

    const textoSolicitudAt = (solicitudAt?.comentario ?? '').trim();
    const solicitudAtComentarios = textoSolicitudAt
        ? [
              {
                  id: 'at-peticion-info',
                  etiqueta: 'Petición de información',
                  usuario: solicitudAt.usuario,
                  fecha_hora: solicitudAt.fecha_hora,
                  comentario: textoSolicitudAt,
              },
          ]
        : [];

    const handleSubmit = useCallback(async () => {
        const texto = informacion.trim();

        if (!texto) {
            toast.error('La información solicitada es obligatoria.');
            return;
        }

        setSubmitting(true);
        try {
            const res = await SOLICITUD_ALTERNATIVAS_API.enviarMasInformacion({
                solicitud_id: currentItem.id,
                informacion: texto,
            });

            if (!res?.success) {
                toast.error(res?.message || 'No se ha podido enviar la información.');
                return;
            }

            toast.success(
                res?.message ||
                    'Información enviada. La solicitud vuelve a estar en revisión.',
            );
            onSolicitudUpdated?.(res.data);
            onClose();
        } catch (e) {
            toast.error(
                e?.response?.data?.message ||
                    e?.message ||
                    'Error al enviar la información.',
            );
        } finally {
            setSubmitting(false);
        }
    }, [currentItem?.id, informacion, onClose, onSolicitudUpdated]);

    if (loading) {
        return <LoadingInline />;
    }

    return (
        <div>
            <ModalSection title="Información solicitada por at. cliente">
                <ModalPanel className="card p-3 p-md-4 mb-4">
                    {solicitudAtComentarios.length ? (
                        <ComentariosHistorialList
                            comentarios={solicitudAtComentarios}
                        />
                    ) : (
                        <span
                            style={{
                                color: '#9e9e9e',
                                fontSize: '0.85rem',
                            }}
                        >
                            Sin detalle de la solicitud de información.
                        </span>
                    )}
                </ModalPanel>
            </ModalSection>

            {sinAlternativa ? (
                <ModalSection title="Sin alternativa">
                    <ModalPanel className="card p-3 p-md-4 mb-4">
                        <SinAlternativaDetallePanel
                            sinAlternativa={sinAlternativa}
                        />
                    </ModalPanel>
                </ModalSection>
            ) : alternativa ? (
                <ModalSection title="Alternativa propuesta">
                    <ModalPanel className="card p-3 p-md-4 mb-4">
                        <AlternativaDetalleAlternativasGrid
                            variant="minimal"
                            alternativa={alternativa}
                        />
                    </ModalPanel>
                </ModalSection>
            ) : null}

            {!readOnly ? (
                <section>
                    <ModalSection title="Tu respuesta">
                        <ModalPanel className="card p-3 p-md-4 mb-4">
                            <ActionCommentField
                                label="Información para atención al cliente"
                                placeholder="Escribe la información que se ha solicitado..."
                                value={informacion}
                                onChange={(e) => setInformacion(e.target.value)}
                                required
                            />
                        </ModalPanel>
                    </ModalSection>

                    <div className="p-3 mb-4 rounded" style={NOTICE_BOX_STYLE}>
                        Al guardar, la solicitud volverá a estado{' '}
                        <span className="fw-semibold">En revisión</span> para
                        que atención al cliente pueda aceptar o rechazar la
                        alternativa.
                    </div>

                    <div className="d-flex justify-content-end pt-1">
                        <ActionButton
                            disabled={submitting || !informacion.trim()}
                            loading={submitting}
                            label="Enviar información"
                            onClick={handleSubmit}
                        />
                    </div>
                </section>
            ) : null}
        </div>
    );
};

export default DetalleMasInformacionView;